import { useCSRFToken } from '#admin/contexts/CSRFContext.js';
import { LoginMutation, LoginMutationVariables } from '#admin/generated/client.js';
import { HOME_ROUTE } from '#admin/routes.paths.js';
import { gql, TypedDocumentNode } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Box, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import * as z from 'zod';

const Login: TypedDocumentNode<LoginMutation, LoginMutationVariables> = gql`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      id
      email
    }
  }
`;

const schema = z.object({
  email: z.string().trim().min(1, 'Email is required').email(),
  password: z.string().trim().min(1, 'Password is required'),
});

type FormValues = z.infer<typeof schema>;

const Page = () => {
  const csrfToken = useCSRFToken();
  const client = useApolloClient();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
  });
  const [invalidCredentials, setInvalidCredentials] = React.useState(false);
  const [searchParams] = useSearchParams();
  const next = searchParams.get('next');

  const onSubmit = async (values: FormValues) => {
    try {
      await client.mutate({
        mutation: Login,
        variables: {
          email: values.email,
          password: values.password,
        },
      });
      setInvalidCredentials(false);
      window.location.href = next ?? HOME_ROUTE;
    } catch (err) {
      setInvalidCredentials(true);
    }
  };

  return (
    <>
      <Typography variant="h4" component="h1" sx={{ mb: 4 }}>
        Sign in to your account
      </Typography>
      <Box
        sx={{
          mt: 1,
        }}
      >
        <form noValidate action="/login/" method="post" onSubmit={handleSubmit(onSubmit)}>
          {csrfToken && <input type="hidden" name="_csrf" value={csrfToken} />}
          <Stack
            sx={{
              gap: 2,
            }}
          >
            <TextField
              type="email"
              id="email"
              placeholder="name@company.com"
              label="Your email"
              required
              {...register('email', { required: true, onChange: () => setInvalidCredentials(false) })}
              helperText={errors.email?.message}
              error={Boolean(errors.email || invalidCredentials)}
              autoFocus
            />
            <TextField
              type="password"
              id="password"
              label="Password"
              placeholder="••••••••"
              required
              {...register('password', { required: true, onChange: () => setInvalidCredentials(false) })}
              helperText={errors.password?.message ?? (invalidCredentials ? 'Invalid username or password' : null)}
              error={Boolean(errors.password || invalidCredentials)}
            />
            <Button type="submit" color="primary" variant="contained" loading={isSubmitting}>
              Sign in
            </Button>
          </Stack>
        </form>
      </Box>
    </>
  );
};

export default Page;
